import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Renderer2,
} from '@angular/core';
import { Constants } from '@base/src/app/models/constants';
import { QRButton } from '@base/src/app/models/qr-button.model';
import { QRInput } from '@base/src/app/models/qr-input.model';
import { QRSearchAutocompleteModel } from '@base/src/app/models/search-autocomplete.model';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { SnackbarService } from '@base/src/app/services/snackbar.service';

type InputType = string | number | undefined;

@Component({
  selector: 'qr-search-autocomplete',
  templateUrl: './search-autocomplete.component.html',
  styleUrls: ['./search-autocomplete.component.scss'],
})
export class SearchAutocompleteComponent implements OnInit {
  @Input() config!: QRSearchAutocompleteModel;
  @Input() value?: string = '';
  @Input() hasError?: boolean = false;
  @Output() selected: EventEmitter<string> = new EventEmitter<string>();

  buttonOpen: QRButton = {
    hierarchy: QRButton.HIERARCHY_3,
    size: QRButton.SIZE_MEDIUM,
    icon: Constants.ICONS.SEARCH,
    viewValue: '',
  };

  buttonSelect: QRButton = {
    hierarchy: QRButton.HIERARCHY_1,
    size: QRButton.SIZE_MEDIUM,
    icon: Constants.ICONS.CHECK_2,
    viewValue: 'Seleccionar',
  };

  inputSearch: QRInput = {
    mode: QRInput.MODE_STRING,
    hierarchy: QRInput.HIERARCHY_1,
    size: QRInput.SIZE_MEDIUM,
    placeholder: 'Buscar',
    icon: Constants.ICONS.SEARCH,
  };

  ICON_CLOSE = Constants.ICONS.CLOSE;
  COLOR_GREY_700 = Constants.ICONS.COLOR_GREY_700;

  selection: string = '';
  inputText: string = '';
  chipsList: string[] = [];
  filteredChips: { text: string; highlighted: SafeHtml }[] = [];

  isClosed = true;
  manualSelection = false;

  constructor(
    private readonly sanitizer: DomSanitizer,
    private readonly snackbar: SnackbarService,
    private readonly renderer: Renderer2,
    private readonly elementRef: ElementRef
  ) {}

  ngOnInit(): void {
    if (this.hasError) {
      this.buttonOpen.hierarchy = QRButton.HIERARCHY_ERROR;
    }
    this.renderer.listen('document', 'click', (event: MouseEvent) => {
      this.handleOutsideClick(event);
    });

    if (this.config.buttonText) {
      this.buttonOpen.viewValue = this.config.buttonText;
    }

    if (this.value) {
      this.selection = this.value;
      this.inputText = this.value;
      this.buttonOpen.viewValue = this.value;
    }

    if (this.config.options.length > 0) {
      this.generateChips();
    }
  }

  generateChips() {
    this.chipsList = [...this.config.options];
    this.filteredChips = this.chipsList.map((chip) => ({
      text: chip,
      highlighted: chip,
    }));
  }

  handleOutsideClick(event: MouseEvent): void {
    const IS_CLICK_INSIDE = this.elementRef.nativeElement.contains(
      event.target
    );
    if (!IS_CLICK_INSIDE) {
      this.inputText = '';
      this.generateChips();
      this.isClosed = true; // Close the component if clicked outside
    }
  }

  onOpen() {
    this.isClosed = !this.isClosed;

    setTimeout(() => {
      this.value = '';
      this.selection = '';
      this.buttonOpen.viewValue = this.config.buttonText;

      if (this.isClosed) {
        this.inputText = '';
        this.manualSelection = false;
      }
    }, 0);
  }

  onClickChip(name: string) {
    this.chipsList = [name];
    this.inputText = name;
    this.selection = this.inputText;
  }

  inputSearchChange($event: InputType) {
    const VALUE = $event ? $event.toString().toLowerCase() : '';
    const NORMALIZED_VALUE = this.removeAccents(VALUE);
    if (this.manualSelection) {
      this.manualSelection = false;
    }

    if (this.selection != this.inputText) {
      this.selection = '';
    }

    if (VALUE) {
      this.inputText = $event?.toString() ?? '';
      this.filteredChips = this.config.options
        .filter((chip) =>
          this.removeAccents(chip.toLowerCase()).includes(NORMALIZED_VALUE)
        )
        .map((chip) => ({
          text: chip,
          highlighted: this.getHighlightedText(chip, VALUE),
        }));
    } else {
      this.filteredChips = this.config.options.map((chip) => ({
        text: chip,
        highlighted: chip,
      }));
    }
  }

  removeAccents(text: string): string {
    return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  getHighlightedText(text: string, query: string): SafeHtml {
    const NORMALIZED_TEXT = this.removeAccents(text);
    const NORMALIZED_VALUE = this.removeAccents(query);
    // g matchea todas las ocurrencias no solo la primera.
    // i matchea a pesar de las mayusculas (deberia estar todo en low case pero por las dudas, nunca esta de mas)
    const REGEX = new RegExp(`(${NORMALIZED_VALUE})`, 'gi');
    const HIGHLIGHTED = NORMALIZED_TEXT.replace(REGEX, `<strong>$1</strong>`);
    return this.sanitizer.bypassSecurityTrustHtml(
      text.replace(REGEX, `<strong>$1</strong>`)
    );
    // ¿Por que el $1? Porque representa el primer grupo capturado en la regex, si hubiera dos por ejemplo podria usar $2 para el segundo
  }

  onSelect() {
    if (this.selection) {
      this.selected.emit(this.selection);
      this.isClosed = true;
      this.buttonOpen.viewValue = this.selection;
    } else {
      this.snackbar.openSnackBar('Seleccione una opción para continuar', '');
    }
  }

  onSelectionNew() {
    this.manualSelection = !this.manualSelection;
    this.selection = this.inputText;
  }
}
